import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  message,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Checkbox,
  Popconfirm,
  Tooltip,
  Grid,
  List
} from 'antd';
import {
  EditOutlined,
  UserOutlined,
  KeyOutlined,
  TeamOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const ModuleManager = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [users, setUsers] = useState([]);
  const [userModules, setUserModules] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignForm] = Form.useForm();

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const usersResponse = await axios.get('https://limitverify.ru:5000/api/modules/users-with-modules', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(usersResponse.data);

      const modulesResponse = await axios.get('https://limitverify.ru:5000/api/modules/modules', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModules(modulesResponse.data);

      const userModulesResponse = await axios.get('https://limitverify.ru:5000/api/modules/user-module-relations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserModules(userModulesResponse.data);
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
      message.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const getUserModuleData = (userId) =>
    userModules.filter((um) => um.user_id === userId);

  const handleAssignModules = (user) => {
    setSelectedUser(user);
    const currentUserModules = userModules
      .filter((um) => um.user_id === user.id && um.can_access)
      .map((um) => um.module_name);

    assignForm.setFieldsValue({
      moduleNames: currentUserModules
    });
    setAssignModalVisible(true);
  };

  const handleAssignSubmit = async () => {
    try {
      const values = await assignForm.validateFields();

      await axios.put(`https://limitverify.ru:5000/api/modules/users/${selectedUser.id}/modules`, {
        moduleNames: values.moduleNames
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success('Модули пользователя обновлены успешно');
      setAssignModalVisible(false);
      loadData();
    } catch (error) {
      console.error('Ошибка назначения модулей:', error);
      message.error('Ошибка назначения модулей');
    }
  };

  const handleDelete = async (userId, userName) => {
    try {
      await axios.delete(`https://limitverify.ru:5000/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      message.success(`Пользователь ${userName} удален успешно`);
      loadData();
    } catch (error) {
      console.error('Ошибка удаления пользователя:', error);
      if (error.response?.status === 400 && error.response?.data?.error === 'Cannot delete yourself') {
        message.error('Нельзя удалить самого себя');
      } else if (error.response?.status === 403) {
        message.error('У вас нет прав для удаления пользователей');
      } else {
        message.error('Ошибка удаления пользователя');
      }
    }
  };

  const renderModules = (userId) => {
    const userModuleData = getUserModuleData(userId);
    if (!userModuleData.length) {
      return <Tag color="red">Нет модулей</Tag>;
    }
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {userModuleData.map((um) => (
          <Tag key={um.module_name} color="blue" style={{ margin: 0 }}>
            {um.module_display_name || um.module_name}
          </Tag>
        ))}
      </div>
    );
  };

  const renderActions = (record) => (
    <Space
      size="small"
      wrap
      style={{ width: isMobile ? '100%' : 'auto' }}
      direction={isMobile ? 'vertical' : 'horizontal'}
    >
      <Button
        type="primary"
        icon={<EditOutlined />}
        onClick={() => handleAssignModules(record)}
        block={isMobile}
      >
        Назначить модули
      </Button>
      <Popconfirm
        title="Удалить пользователя?"
        description="Это действие нельзя отменить"
        onConfirm={() => handleDelete(record.id, record.full_name)}
        okText="Да"
        cancelText="Нет"
      >
        <Tooltip title="Удалить">
          <Button danger icon={<DeleteOutlined />} block={isMobile}>
            Удалить
          </Button>
        </Tooltip>
      </Popconfirm>
    </Space>
  );

  const userColumns = [
    {
      title: 'Пользователь',
      key: 'user',
      width: 280,
      render: (_, record) => (
        <Space align="start">
          <UserOutlined style={{ marginTop: 4 }} />
          <div style={{ minWidth: 0 }}>
            <Text strong style={{ wordBreak: 'break-word' }}>{record.full_name}</Text>
            <br />
            <Text type="secondary" style={{ wordBreak: 'break-all' }}>{record.email}</Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Модули',
      key: 'modules',
      render: (_, record) => renderModules(record.id)
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 280,
      render: (_, record) => renderActions(record)
    }
  ];

  return (
    <div>
      <Card styles={{ body: { padding: isMobile ? 12 : 24 } }}>
        <div style={{ marginBottom: 16 }}>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0 }}>
            <TeamOutlined /> Управление модулями пользователей
          </Title>
        </div>

        {isMobile ? (
          <List
            loading={loading}
            dataSource={users}
            pagination={{
              pageSize: 10,
              size: 'small',
              showTotal: (total, range) => `${range[0]}-${range[1]} из ${total}`
            }}
            renderItem={(record) => (
              <Card
                key={record.id}
                size="small"
                style={{ marginBottom: 12 }}
                styles={{ body: { padding: 12 } }}
              >
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
                  <UserOutlined style={{ marginTop: 3, flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <Text strong style={{ display: 'block', wordBreak: 'break-word' }}>
                      {record.full_name}
                    </Text>
                    <Text type="secondary" style={{ display: 'block', wordBreak: 'break-all', fontSize: 13 }}>
                      {record.email}
                    </Text>
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  {renderModules(record.id)}
                </div>
                {renderActions(record)}
              </Card>
            )}
          />
        ) : (
          <Table
            columns={userColumns}
            dataSource={users}
            rowKey="id"
            loading={loading}
            scroll={{ x: 800 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} пользователей`
            }}
          />
        )}
      </Card>

      <Modal
        title={`Назначить модули: ${selectedUser?.full_name || ''}`}
        open={assignModalVisible}
        onOk={handleAssignSubmit}
        onCancel={() => setAssignModalVisible(false)}
        width={isMobile ? '92%' : 600}
        styles={{ body: { maxHeight: isMobile ? '70vh' : undefined, overflow: 'auto' } }}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item
            name="moduleNames"
            label="Выберите модули:"
            rules={[{ required: true, message: 'Выберите хотя бы один модуль' }]}
          >
            <Checkbox.Group style={{ width: '100%' }}>
              <Row gutter={[12, 8]}>
                {modules.map((module) => (
                  <Col xs={24} sm={12} key={module.name}>
                    <Checkbox value={module.name}>
                      <Space>
                        <KeyOutlined />
                        {module.displayName || module.name}
                      </Space>
                    </Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ModuleManager;
